import { useNavigate, useParams } from "@tanstack/react-router";

import { useBumpRecipeVersion, useRecipe } from "@/lib/api/recipes";
import { ErrorState } from "@/components/error-state";

import { RecipeForm, emptyToUndef, type RecipeFormValues } from "./form";

// Bump a recipe to a new immutable version. The recipe ID stays the same;
// the prior version is preserved unchanged so historical refills resolve.
// The form pre-populates from the latest version so the user only has to
// edit the diff.

export function RecipeBumpPage() {
  const { recipeId } = useParams({
    from: "/chemistry-profile/recipes/$recipeId/bump",
  });
  const navigate = useNavigate();
  const q = useRecipe(recipeId);
  const bump = useBumpRecipeVersion(recipeId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  if (q.data.system_provided) {
    return (
      <p className="text-sm text-muted-foreground">
        System recipes are read-only; clone to a tenant copy first.
      </p>
    );
  }

  function submit(values: RecipeFormValues) {
    bump.mutate(
      {
        name: values.name,
        description: emptyToUndef(values.description),
        entries: values.entries.map((e) => ({
          order_rank: Number(e.order_rank),
          nutrient_id: e.nutrient_id,
          ml_per_gal: Number(e.ml_per_gal),
          notes: emptyToUndef(e.notes),
        })),
        reason: emptyToUndef(values.reason),
      },
      {
        onSuccess: () => navigate({ to: "/chemistry-profile/recipes" }),
      },
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        Bump recipe: <span className="text-muted-foreground">{q.data.name}</span>{" "}
        <span className="text-sm text-muted-foreground">
          (v{q.data.version} → v{q.data.version + 1})
        </span>
      </h1>
      <RecipeForm
        initial={q.data}
        submitting={bump.isPending}
        showReason
        submitLabel="Create new version"
        onSubmit={submit}
        banner={
          bump.error ? (
            <ErrorState error={bump.error} retry={() => bump.reset()} />
          ) : undefined
        }
      />
    </div>
  );
}
